import { create } from 'zustand';
import { isAxiosError } from 'axios';
import { useCases } from '../../infrastructure/di/container';
import { getErrorMessage } from '../../infrastructure/http/common';
import { ChannelEntity } from '../../domain/entities/Channel';
import { ChannelFilters } from '../../shared/types/ChannelFilters';
import { CreateAdminChannelInput, UpdateAdminChannelInput } from '../../application/ports/IAdminChannelRepository';
import { useChannelCategoriesStore } from './channelCategories.store';

export const ADMIN_CHANNELS_DEFAULT_FILTERS: ChannelFilters = {
  search: '',
  categories: [],
  types: [],
  sortBy: 'membersCount',
  sortOrder: 'desc',
};

const toErrorSlug = (e: unknown): string => {
  if (isAxiosError(e)) return getErrorMessage(e);
  if (e instanceof Error && e.message) return e.message;
  return 'unknown-error';
};

interface AdminChannelsState {
  items: ChannelEntity[];
  total: number;
  page: number;
  perPage: number;
  filters: ChannelFilters;
  isLoading: boolean;
  isSaving: boolean;
  error?: string;
  load: () => Promise<void>;
  setFilters: (filters: ChannelFilters) => Promise<void>;
  setPage: (page: number, perPage: number) => Promise<void>;
  createChannel: (input: CreateAdminChannelInput) => Promise<void>;
  updateChannel: (uuid: string, input: UpdateAdminChannelInput) => Promise<void>;
  createCategory: (name: string) => Promise<void>;
  renameCategory: (uuid: string, name: string) => Promise<void>;
}

export const useAdminChannelsStore = create<AdminChannelsState>((set, get) => {
  const mutate = async (action: () => Promise<unknown>, reloadChannels: boolean) => {
    set({ isSaving: true });
    try {
      await action();
    } catch (e) {
      throw new Error(toErrorSlug(e));
    } finally {
      set({ isSaving: false });
    }
    if (reloadChannels) await get().load();
  };

  return {
    items: [],
    total: 0,
    page: 1,
    perPage: 20,
    filters: ADMIN_CHANNELS_DEFAULT_FILTERS,
    isLoading: false,
    isSaving: false,
    async load() {
      const { page, perPage, filters } = get();
      set({ isLoading: true, error: undefined });
      try {
        const result = await useCases.listAdminChannels().execute({ page, perPage, ...filters });
        set({ items: result.items, total: result.total, isLoading: false });
      } catch (e) {
        set({ isLoading: false, error: toErrorSlug(e) });
      }
    },
    async setFilters(filters) {
      set({ filters, page: 1 });
      await get().load();
    },
    async setPage(page, perPage) {
      set({ page, perPage });
      await get().load();
    },
    createChannel(input) {
      return mutate(() => useCases.createAdminChannel().execute(input), true);
    },
    updateChannel(uuid, input) {
      return mutate(() => useCases.updateAdminChannel().execute(uuid, input), true);
    },
    async createCategory(name) {
      await mutate(() => useCases.createChannelCategory().execute(name), false);
      await useChannelCategoriesStore.getState().load(true);
    },
    async renameCategory(uuid, name) {
      await mutate(() => useCases.renameChannelCategory().execute(uuid, name), true);
      await useChannelCategoriesStore.getState().load(true);
    },
  };
});
